import { useContext, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useIdleTimer } from "react-idle-timer"
import { logOutAction } from "../redux/modules/users"
import {
  getInactivityLogOutMessage,
  setInactivityLogOutMessage,
  removeInactivityLogOutMessage
} from "./helpers"
import { useGenericReducer } from "../hooks/useGenericReducer"
import { enqueueSnackbar, closeSnackbar } from "notistack"
import {
  INACTIVITY_LOG_OUT_SNACKBAR,
  THROTTLE_DURATION,
  INACTIVITY_DURATION,
  INACTIVITY_LOG_OUT_MESSAGE,
  TAB_INACTIVITY_TIMER_NAME,
  SYNC_TAB_TIMERS_DURATION,
  TEST_LOG_OUT_TIMER_DURATION,
  LOG_OUT_TIMER_DURATION,
  TEST_INACTIVITY_DURATION,
  TEST_THROTTLE_DURATION
} from "./constants"
import { useLocation } from "react-router-dom"
import { RootState } from "../redux/store"
import { User } from "../../mp-database"
import {
  EventCategory,
  EventAction,
  EventLabel
} from "../../mp-utils/generic-utils"
import { generateEventMetadata, mapUserRolesWithLabels, faLogEvent } from "../../mp-utils/generic-utils/Events/helpers"
import { trackEvent } from "../../mp-utils/generic-utils/Events/eventService"
import { FeatureFlagContext } from "../FeatureFlagProvider/FeatureFlagProvider"
import { FeatureFlagName } from "../../mp-utils/generic-utils/feature-flags/types"

type TabTimerMessageData = {
  openLogOutTimer?: boolean
  snackbarMessage?: string
}

const useSelectDuration = () => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)

  const isTesting = searchParams.get("testIdle") === "true"

  return {
    logOutTimerDuration: isTesting
      ? TEST_LOG_OUT_TIMER_DURATION
      : LOG_OUT_TIMER_DURATION,
    inactivityDuration: isTesting
      ? TEST_INACTIVITY_DURATION
      : INACTIVITY_DURATION,
    throttleDuration: isTesting ? TEST_THROTTLE_DURATION : THROTTLE_DURATION
  }
}

export const useSessionManage = (isAuthed: boolean) => {
  const dispatch = useDispatch() as any
  const user = useSelector((state: RootState) => state.users.testUser as User)

  const { featureFlags } = useContext(FeatureFlagContext)

  const [openLogOutTimer, setOpenLogOutTimer] = useState<boolean>(false)

  const { state: loading, stateSetter: loadingSetter } = useGenericReducer({
    logOutLoading: false
  })

  // Open log out timer dialog and pause inactivity timer
  const onOpenLogOutTimer = () => {
    pause()
    setOpenLogOutTimer(true)
  }

  // Close log out timer dialog and send message to other timers on other tabs about closing
  const onCloseLogOutTimer = () => {
    setOpenLogOutTimer(false)
    message({ openLogOutTimer: false })
  }

  const onLogOut = async () => {
    loadingSetter({ type: "logOutLoading", value: true })

    faLogEvent(
      EventCategory.AUTH,
      EventAction.LOGOUT_INACTIVITY,
      EventLabel.REPORTING,
      generateEventMetadata({
        userId: user.id,
        userRole: user.role,
        eventAction: EventAction.LOGOUT,
        districtId: user.schoolDistrict,
        schoolId: user.school
      })
    )

    trackEvent({
      type: EventAction.LOGOUT_INACTIVITY,
      category: EventCategory.AUTH,
      label: EventLabel.REPORTING,
    }, "fa", generateEventMetadata({
      userId: user.id,
      userRole: user.role,
      eventAction: EventAction.LOGOUT,
      districtId: user.schoolDistrict,
      schoolId: user.school,
    }))

    faLogEvent(
      EventCategory.AUTH,
      EventAction.LOGOUT_INACTIVITY,
      mapUserRolesWithLabels(user?.role),
      {
        "user.id": user.id
      }
    )

    trackEvent({
      type: EventAction.LOGOUT_INACTIVITY,
      category: EventCategory.AUTH,
      label: mapUserRolesWithLabels(user?.role),
    }, "fa", {
      "user.id": user.id,
    })

    dispatch(await logOutAction())

    onCloseLogOutTimer()

    loadingSetter({ type: "logOutLoading", value: false })
  }

  // Continue session and activate inactivity timer from initial state
  const onContinue = async () => {
    activate()
    onCloseLogOutTimer()
  }

  // Do log out action, set inactivity message and
  // send message to other timers on other tabs about inactivity message
  const onLogOutTimerComplete = () => {
    onLogOut()

    message({ snackbarMessage: INACTIVITY_LOG_OUT_MESSAGE })
    setInactivityLogOutMessage(INACTIVITY_LOG_OUT_MESSAGE)
  }

  // Handler of messages from other tabs
  const onTabTimerMessage = (data: TabTimerMessageData) => {
    if (data.hasOwnProperty("openLogOutTimer")) {
      setOpenLogOutTimer(data.openLogOutTimer)
    }

    if (data.hasOwnProperty("snackbarMessage")) {
      setInactivityLogOutMessage(data.snackbarMessage)
    }
  }

  const { logOutTimerDuration, inactivityDuration, throttleDuration } =
    useSelectDuration()

  // Hook for checking user's inactivity
  const { start, pause, activate, message } = useIdleTimer({
    onIdle: onOpenLogOutTimer,
    onAction: onCloseLogOutTimer,
    onMessage: onTabTimerMessage,
    crossTab: true,
    startManually: true,
    timeout: inactivityDuration,
    throttle: throttleDuration,
    syncTimers: SYNC_TAB_TIMERS_DURATION,
    name: TAB_INACTIVITY_TIMER_NAME
  })

  useEffect(() => {
    if (!isAuthed) return
    if (!featureFlags?.[FeatureFlagName.idle_logout]) return

    start()
  }, [isAuthed, featureFlags?.[FeatureFlagName.idle_logout]])

  // Hook for managing snackbar after system log out
  useEffect(() => {
    if (isAuthed) {
      removeInactivityLogOutMessage()
      closeSnackbar(INACTIVITY_LOG_OUT_SNACKBAR)

      return
    }

    const logOutSnackbarMessage = getInactivityLogOutMessage()

    if (!logOutSnackbarMessage) return

    enqueueSnackbar(logOutSnackbarMessage, {
      key: INACTIVITY_LOG_OUT_SNACKBAR,
      persist: true,
      variant: "info",
      preventDuplicate: true,
      anchorOrigin: { vertical: "top", horizontal: "center" },
      onExited: () => removeInactivityLogOutMessage()
    })
  }, [isAuthed])

  return {
    logOutTimerDuration,
    openLogOutTimer,
    loading,
    onLogOut,
    onContinue,
    onLogOutTimerComplete
  }
}
